import React from 'react';
import Input from 'formik-antd/lib/input';
import Select from 'formik-antd/lib/select';
import { Switch } from './Switch';
import Form from 'formik-antd/lib/form';
import { TFunction } from 'i18next';
import {
  Flex,
  KeyValue,
  Grid,
  Segment,
  FormGroup,
  RemoteAccessRDPProfile,
  rdpValidationSchema,
} from '@mss/mss-component-library';
import { Formik, ErrorMessage } from 'formik';
import emptyProfiles from './emptyProfiles';
import timezonesIANA from 'jsx/common/util/timezones';
import { useTranslation } from 'react-i18next';
import SubmitButton from 'jsx/common/SubmitButton';
import Button from 'jsx/common/Button';
import { Trash } from 'react-feather';
import ToggleEnabled from './ToggleEnabled';
import colordepths from './colordepths';
import { ProfileCollapse } from './ProfileCollapse';

const rdpSecurityModes = (t: TFunction): KeyValue[] => [
  { value: 'any', key: t('Any') },
  { value: 'nla', key: t('Network Level Authentication') },
  { value: 'nla-ext', key: t('Network Level Authentication Extended') },
  { value: 'tls', key: t('TLS Encryption') },
  { value: 'vmconnect', key: t('Hyper-V / VMConnect') },
  { value: 'rdp', key: t('RDP Encryption') },
];

const rdpServerLayout = (t: TFunction): KeyValue[] => [
  { value: 'any', key: t('Any') },
  { value: 'en-us-qwerty', key: t('English (US) keyboard') },
  { value: 'en-gb-qwerty', key: t('English (UK) keyboard') },
  { value: 'de-ch-qwerty', key: t('Swiss German keyboard (qwertz)') },
  { value: 'de-de-qwertz', key: t('German keyboard (qwertz)') },
  { value: 'fr-be-azerty', key: t('Belgian French keyboard') },
  { value: 'fr-fr-azerty', key: t('French keyboard (azerty)') },
  { value: 'fr-ch-qwertz', key: t('Swiss French keyboard (qwertz)') },
  { value: 'hu-hu-qwertz', key: t('Hungarian keyboard (qwertz)') },
  { value: 'it-it-qwerty', key: t('Italian keyboard') },
  { value: 'ja-jp-qwerty', key: t('Japanese keyboard') },
  { value: 'pt-br-qwerty', key: t('Portuguese Brazilian keyboard') },
  { value: 'es-es-qwerty', key: t('Spanish keyboard') },
  { value: 'es-latam-qwerty', key: t('Latin American keyboard') },
  { value: 'sv-se-qwerty', key: t('Swedish keyboard') },
  { value: 'tr-tr-qwerty', key: t('Turkish-Q keyboard') },
  { value: 'failsafe', key: t('Unicode') },
];

const rdpClipboardNormalization = (t: TFunction): KeyValue[] => [
  { value: 'windows', key: t('Windows') },
  { value: 'unix', key: t('Unix') },
  { value: 'preserve', key: t('None') },
];
type RDPCollapseProps = {
  onSubmit: (values: any) => any;
  profile?: RemoteAccessRDPProfile & { id: string };
  asset: string;
  deleteProfile?: (id: string) => void;
  readonly?: boolean;
};

const RDPCollapse = ({
  onSubmit,
  profile,
  asset,
  deleteProfile,
  readonly,
}: RDPCollapseProps) => {
  const [t] = useTranslation(['remoteAccess', 'common', 'asset']);
  const toggleEnabled = () => {
    if (!readonly) onSubmit({ ...profile, enabled: !profile?.enabled });
  };
  return (
    <Formik
      validationSchema={rdpValidationSchema(t)}
      onSubmit={onSubmit}
      initialValues={
        profile
          ? { ...emptyProfiles.rdp, ...profile, asset }
          : { ...emptyProfiles.rdp, asset }
      }
    >
      {({ values, isSubmitting, errors, setFieldValue, setFieldTouched }) => {
        const hasErrors = Object.keys(errors).length > 0;

        return (
          <ProfileCollapse
            defaultActiveKey={profile ? undefined : 'rdp'}
            bordered={false}
            expandIconPosition="end"
          >
            <ProfileCollapse.Panel
              key="rdp"
              header={
                <span>
                  {t('RDP')} - {values.name}{' '}
                  {!profile && (
                    <strong style={{ color: 'red' }}>{t('Unsaved')}</strong>
                  )}
                </span>
              }
              extra={
                <ToggleEnabled
                  setFieldValue={setFieldValue}
                  t={t}
                  checked={values.enabled}
                  onSubmit={profile ? toggleEnabled : undefined}
                  disabled={readonly}
                />
              }
            >
              <Form>
                <Grid.Container>
                  <Segment
                    heading={
                      <Flex v="center" h={deleteProfile ? 'between' : 'start'}>
                        <span>{t('Connection Settings')}</span>
                        {deleteProfile && profile && (
                          <Button
                            size="small"
                            onClick={() => deleteProfile(profile.id)}
                            data-ta="RDPCollapseDeleteButton"
                          >
                            <Flex v="center">
                              <Trash size={14} className="mr-1" />
                              {t('Delete')}
                            </Flex>
                          </Button>
                        )}
                      </Flex>
                    }
                  >
                    <Grid.Row>
                      <Grid.Col width={4}>
                        <FormGroup
                          required
                          error={<ErrorMessage name="name" />}
                          label={t('Name')}
                          input={
                            <Input
                              disabled={readonly}
                              name="name"
                              value={values.name}
                              onInput={() =>
                                setFieldTouched(`name`, true, true)
                              }
                            />
                          }
                        />
                      </Grid.Col>
                      <Grid.Col width={4}>
                        <FormGroup
                          error={<ErrorMessage name="settings.port" />}
                          label={t('Port')}
                          input={
                            <Input
                              disabled={readonly}
                              name="settings.port"
                              type="number"
                              value={values.settings.port || ''}
                            />
                          }
                        />
                      </Grid.Col>
                    </Grid.Row>
                  </Segment>
                  <Segment heading={t('Authentication Settings')}>
                    <Grid.Row>
                      <Grid.Col width={4}>
                        <FormGroup
                          error={<ErrorMessage name="settings.domain" />}
                          label={t('Domain')}
                          input={
                            <Input
                              disabled={readonly}
                              name="settings.domain"
                              value={values.settings.domain}
                            />
                          }
                        />
                      </Grid.Col>
                      <Grid.Col width={4}>
                        <FormGroup
                          error={<ErrorMessage name="settings.username" />}
                          label={t('Username')}
                          input={
                            <Input
                              disabled={readonly}
                              name="settings.username"
                              value={values.settings.username}
                            />
                          }
                        />
                      </Grid.Col>
                      <Grid.Col width={4}>
                        <FormGroup
                          error={<ErrorMessage name="settings.password" />}
                          label={t('Password')}
                          input={
                            <Input.Password
                              disabled={readonly}
                              name="settings.password"
                              defaultValue={
                                values.settings_meta.secrets.password
                                  ? '*******************'
                                  : ''
                              }
                              allowClear={true}
                              visibilityToggle={true}
                              onChange={val => {
                                setFieldValue(
                                  'settings.password',
                                  val.target.value
                                );
                              }}
                            />
                          }
                        />
                      </Grid.Col>
                      <Grid.Col width={4}>
                        <FormGroup
                          error={<ErrorMessage name="settings.security" />}
                          label={t('Security Mode')}
                          input={
                            <Select
                              disabled={readonly}
                              name="settings.security"
                              style={{ width: '100%' }}
                              placeholder={`${t('Please select one')}...`}
                            >
                              {rdpSecurityModes(t).map((mode: KeyValue) => (
                                <Select.Option
                                  disabled={readonly}
                                  key={mode.key}
                                  value={mode.value}
                                >
                                  {mode.key}
                                </Select.Option>
                              ))}
                            </Select>
                          }
                        />
                      </Grid.Col>
                      <Grid.Col width={4}>
                        <FormGroup
                          error={<ErrorMessage name="settings.disable-auth" />}
                          label={t('Disable authentication')}
                          input={
                            <Switch
                              disabled={readonly}
                              name="settings.disable-auth"
                              size="small"
                            />
                          }
                        />
                      </Grid.Col>
                      <Grid.Col width={4}>
                        <FormGroup
                          error={<ErrorMessage name="settings.ignore-cert" />}
                          label={t('Ignore server certificate')}
                          input={
                            <Switch
                              disabled={readonly}
                              name="settings.ignore-cert"
                              size="small"
                            />
                          }
                        />
                      </Grid.Col>
                    </Grid.Row>
                  </Segment>
                  <Segment heading={t('Session Recording Settings')}>
                    <Grid.Row>
                      <Grid.Col width={3}>
                        <FormGroup
                          label={t('Record session')}
                          input={
                            <Switch
                              name="record_session"
                              size="small"
                              data-ta="RecordSessionSwitch"
                            />
                          }
                        />
                      </Grid.Col>
                      <Grid.Col width={3}>
                        <FormGroup
                          label={t('Exclude output')}
                          input={
                            <Switch
                              name="settings.recording-exclude-output"
                              disabled={!values.record_session}
                              size="small"
                            />
                          }
                        />
                      </Grid.Col>
                      <Grid.Col width={3}>
                        <FormGroup
                          label={t('Exclude mouse')}
                          input={
                            <Switch
                              name="settings.recording-exclude-mouse"
                              disabled={!values.record_session}
                              size="small"
                            />
                          }
                        />
                      </Grid.Col>
                      <Grid.Col width={3}>
                        <FormGroup
                          label={t('Include keys')}
                          input={
                            <Switch
                              name="settings.recording-include-keys"
                              disabled={!values.record_session}
                              size="small"
                            />
                          }
                        />
                      </Grid.Col>
                    </Grid.Row>
                  </Segment>
                  <ProfileCollapse
                    bordered={false}
                    expandIconPosition="end"
                    defaultActiveKey={[]}
                  >
                    <ProfileCollapse.Panel
                      key={'extraOptions'}
                      header={t('Extra options')}
                      forceRender
                    >
                      <Segment heading={t('Remote Desktop Gateway')}>
                        <Grid.Row>
                          <Grid.Col width={4}>
                            <FormGroup
                              error={
                                <ErrorMessage name="settings.gateway-hostname" />
                              }
                              label={t('Hostname')}
                              input={
                                <Input
                                  disabled={readonly}
                                  name="settings.gateway-hostname"
                                  value={
                                    values.settings['gateway-hostname'] || ''
                                  }
                                />
                              }
                            />
                          </Grid.Col>
                          <Grid.Col width={4}>
                            <FormGroup
                              error={
                                <ErrorMessage name="settings.gateway-port" />
                              }
                              label={t('Port')}
                              input={
                                <Input
                                  disabled={readonly}
                                  name="settings.gateway-port"
                                  type="number"
                                  value={values.settings['gateway-port'] || ''}
                                />
                              }
                            />
                          </Grid.Col>
                          <Grid.Col width={4}>
                            <FormGroup
                              error={
                                <ErrorMessage name="settings.gateway-domain" />
                              }
                              label={t('Domain')}
                              input={
                                <Input
                                  disabled={readonly}
                                  name="settings.gateway-domain"
                                  value={
                                    values.settings['gateway-domain'] || ''
                                  }
                                />
                              }
                            />
                          </Grid.Col>
                          <Grid.Col width={4}>
                            <FormGroup
                              error={
                                <ErrorMessage name="settings.gateway-username" />
                              }
                              label={t('Username')}
                              input={
                                <Input
                                  disabled={readonly}
                                  name="settings.gateway-username"
                                  value={
                                    values.settings['gateway-username'] || ''
                                  }
                                />
                              }
                            />
                          </Grid.Col>
                          <Grid.Col width={4}>
                            <FormGroup
                              error={
                                <ErrorMessage name="settings.gateway-password" />
                              }
                              label={t('Password')}
                              input={
                                <Input
                                  disabled={readonly}
                                  name="settings.gateway-password"
                                  value={
                                    values.settings['gateway-password'] || ''
                                  }
                                />
                              }
                            />
                          </Grid.Col>
                        </Grid.Row>
                      </Segment>
                      <Segment heading={t('Basic settings')}>
                        <Grid.Row>
                          <Grid.Col width={8}>
                            <FormGroup
                              error={
                                <ErrorMessage name="settings.initial-program" />
                              }
                              label={t('Initial program')}
                              input={
                                <Input
                                  disabled={readonly}
                                  name="settings.initial-program"
                                  value={
                                    values.settings['initial-program'] || ''
                                  }
                                />
                              }
                            />
                          </Grid.Col>
                          <Grid.Col width={4}>
                            <FormGroup
                              error={
                                <ErrorMessage name="settings.client-name" />
                              }
                              label={t('Client name')}
                              input={
                                <Input
                                  disabled={readonly}
                                  name="settings.client-name"
                                  value={values.settings['client-name'] || ''}
                                />
                              }
                            />
                          </Grid.Col>
                          <Grid.Col width={4}>
                            <FormGroup
                              error={
                                <ErrorMessage name="settings.server-layout" />
                              }
                              label={t('Keyboard layout')}
                              input={
                                <Select
                                  disabled={readonly}
                                  name="settings.server-layout"
                                  style={{ width: '100%' }}
                                  placeholder={`${t('Please select one')}...`}
                                >
                                  {rdpServerLayout(t).map((mode: KeyValue) => (
                                    <Select.Option
                                      disabled={readonly}
                                      key={mode.key}
                                      value={mode.value}
                                    >
                                      {mode.key}
                                    </Select.Option>
                                  ))}
                                </Select>
                              }
                            />
                          </Grid.Col>
                          <Grid.Col width={4}>
                            <FormGroup
                              error={<ErrorMessage name="settings.timezone" />}
                              label={t('Timezone')}
                              input={
                                <Select
                                  disabled={readonly}
                                  showSearch
                                  optionFilterProp="children"
                                  name="settings.timezone"
                                  style={{ width: '100%' }}
                                  placeholder={`${t('Please select one')}...`}
                                >
                                  {timezonesIANA.map((zone: string) => (
                                    <Select.Option
                                      disabled={readonly}
                                      key={zone}
                                      value={zone}
                                    >
                                      {zone}
                                    </Select.Option>
                                  ))}
                                </Select>
                              }
                            />
                          </Grid.Col>
                          <Grid.Col width={4}>
                            <FormGroup
                              error={<ErrorMessage name="settings.console" />}
                              label={t('Administrator console')}
                              input={
                                <Switch
                                  disabled={readonly}
                                  name="settings.console"
                                  size="small"
                                />
                              }
                            />
                          </Grid.Col>
                        </Grid.Row>
                      </Segment>
                      <Segment heading={t('Display')}>
                        <Grid.Row>
                          <Grid.Col width={4}>
                            <FormGroup
                              error={<ErrorMessage name="settings.width" />}
                              label={t('Width')}
                              input={
                                <Input
                                  disabled={readonly}
                                  name="settings.width"
                                  type="number"
                                  value={values.settings['width'] || ''}
                                  suffix="px"
                                />
                              }
                            />
                          </Grid.Col>
                          <Grid.Col width={4}>
                            <FormGroup
                              error={<ErrorMessage name="settings.height" />}
                              label={t('Height')}
                              input={
                                <Input
                                  disabled={readonly}
                                  name="settings.height"
                                  type="number"
                                  value={values.settings['height'] || ''}
                                  suffix="px"
                                />
                              }
                            />
                          </Grid.Col>
                          <Grid.Col width={4}>
                            <FormGroup
                              error={<ErrorMessage name="settings.dpi" />}
                              label={t('DPI')}
                              input={
                                <Input
                                  disabled={readonly}
                                  name="settings.dpi"
                                  type="number"
                                  value={values.settings['dpi'] || ''}
                                />
                              }
                            />
                          </Grid.Col>
                          <Grid.Col width={4}>
                            <FormGroup
                              error={
                                <ErrorMessage name="settings.color-depth" />
                              }
                              label={t('Color depth')}
                              input={
                                <Select
                                  disabled={readonly}
                                  name="settings.color-depth"
                                  style={{ width: '100%' }}
                                  placeholder={`${t('Please select one')}...`}
                                >
                                  {colordepths.map(colordepth => (
                                    <Select.Option
                                      disabled={readonly}
                                      key={colordepth.key}
                                      value={colordepth.value}
                                    >
                                      {colordepth.key}
                                    </Select.Option>
                                  ))}
                                </Select>
                              }
                            />
                          </Grid.Col>
                          <Grid.Col width={4}>
                            <FormGroup
                              error={
                                <ErrorMessage name="settings.resize-method" />
                              }
                              label={t('Resize method')}
                              input={
                                <Select
                                  disabled={readonly}
                                  name="settings.resize-method"
                                  style={{ width: '100%' }}
                                  placeholder={`${t('Please select one')}...`}
                                >
                                  {[
                                    {
                                      key: 'Display Update (RDP 8.1+)',
                                      value: 'display-update',
                                    },
                                    { key: 'Reconnect', value: 'reconnect' },
                                  ].map((resizemethod: KeyValue) => (
                                    <Select.Option
                                      disabled={readonly}
                                      key={resizemethod.key}
                                      value={resizemethod.value}
                                    >
                                      {resizemethod.key}
                                    </Select.Option>
                                  ))}
                                </Select>
                              }
                            />
                          </Grid.Col>
                          {/* <Grid.Col width={4}>
                        <FormGroup error={
            <ErrorMessage name="settings.read-only" />
          }
                          label={t('Read only')}
                          input={
                            <Switch disabled={readonly} name="settings.read-only" size="small" />
                          }
                        />
                      </Grid.Col> */}
                        </Grid.Row>
                      </Segment>
                      <Segment heading={t('Device redirection')}>
                        <Grid.Row>
                          <Grid.Col width={6}>
                            <table>
                              <tbody>
                                <tr>
                                  <td>
                                    <Switch
                                      disabled={readonly}
                                      name="settings.console-audio"
                                      size="small"
                                    />
                                  </td>
                                  <td>{t('Support audio in console')}</td>
                                </tr>
                                <tr>
                                  <td>
                                    <Switch
                                      disabled={readonly}
                                      name="settings.disable-audio"
                                      size="small"
                                    />
                                  </td>
                                  <td>{t('Disable audio')}</td>
                                </tr>
                              </tbody>
                            </table>
                          </Grid.Col>
                          <Grid.Col width={6}>
                            <table>
                              <tbody>
                                <tr>
                                  <td>
                                    <Switch
                                      disabled={readonly}
                                      name="settings.enable-audio-input"
                                      size="small"
                                    />
                                  </td>
                                  <td>{t('Enable audio input')}</td>
                                </tr>
                                <tr>
                                  <td>
                                    <Switch
                                      disabled={readonly}
                                      name="settings.enable-printing"
                                      size="small"
                                    />
                                  </td>
                                  <td>{t('Enable printing')}</td>
                                </tr>
                              </tbody>
                            </table>
                          </Grid.Col>
                          <Grid.Col width={6} className="mt-4">
                            <FormGroup
                              error={
                                <ErrorMessage name="settings.printer-name" />
                              }
                              label={t('Redirected printer name')}
                              input={
                                <Input
                                  disabled={
                                    readonly ||
                                    !values.settings['enable-printing']
                                  }
                                  name="settings.printer-name"
                                  value={values.settings['printer-name'] || ''}
                                />
                              }
                            />
                          </Grid.Col>
                        </Grid.Row>
                      </Segment>
                      <Segment heading={t('Clipboard')}>
                        <Grid.Row>
                          <Grid.Col width={6}>
                            <table>
                              <tbody>
                                <tr>
                                  <td>
                                    <Switch
                                      disabled={readonly}
                                      name="settings.disable-copy"
                                      size="small"
                                    />
                                  </td>
                                  <td>
                                    {t('Disable copying from remote desktop')}
                                  </td>
                                </tr>
                                <tr>
                                  <td>
                                    <Switch
                                      disabled={readonly}
                                      name="settings.disable-paste"
                                      size="small"
                                    />
                                  </td>
                                  <td>{t('Disable pasting from client')}</td>
                                </tr>
                              </tbody>
                            </table>
                            <FormGroup
                              className="mt-4"
                              error={
                                <ErrorMessage name="settings.normalize-clipboard" />
                              }
                              label={t('Normalize clipboard')}
                              input={
                                <Select
                                  disabled={readonly}
                                  name="settings.normalize-clipboard"
                                  style={{ width: '100%' }}
                                  placeholder={`${t('Please select one')}...`}
                                >
                                  {rdpClipboardNormalization(t).map(
                                    (mode: KeyValue) => (
                                      <Select.Option
                                        disabled={readonly}
                                        key={mode.key}
                                        value={mode.value}
                                      >
                                        {mode.key}
                                      </Select.Option>
                                    )
                                  )}
                                </Select>
                              }
                            />
                          </Grid.Col>
                        </Grid.Row>
                      </Segment>
                      <Segment heading={t('Performance')}>
                        <Grid.Row>
                          <Grid.Col width={6}>
                            <table>
                              <tbody>
                                <tr>
                                  <td>
                                    <Switch
                                      disabled={readonly}
                                      name="settings.enable-wallpaper"
                                      size="small"
                                    />
                                  </td>
                                  <td>{t('Enable wallpaper')}</td>
                                </tr>
                                <tr>
                                  <td>
                                    <Switch
                                      disabled={readonly}
                                      name="settings.enable-theming"
                                      size="small"
                                    />
                                  </td>
                                  <td>{t('Enable theme')}</td>
                                </tr>
                                <tr>
                                  <td>
                                    <Switch
                                      disabled={readonly}
                                      name="settings.enable-font-smoothing"
                                      size="small"
                                    />
                                  </td>
                                  <td>{t('Enable font smoothing')}</td>
                                </tr>
                                <tr>
                                  <td>
                                    <Switch
                                      disabled={readonly}
                                      name="settings.enable-full-window-drag"
                                      size="small"
                                    />
                                  </td>
                                  <td>{t('Enable full-window drag')}</td>
                                </tr>
                                <tr>
                                  <td>
                                    <Switch
                                      disabled={readonly}
                                      name="settings.enable-desktop-composition"
                                      size="small"
                                    />
                                  </td>
                                  <td>{t('Enable desktop composition')}</td>
                                </tr>
                                <tr>
                                  <td>
                                    <Switch
                                      disabled={readonly}
                                      name="settings.enable-menu-animations"
                                      size="small"
                                    />
                                  </td>
                                  <td>{t('Enable menu animations')}</td>
                                </tr>
                              </tbody>
                            </table>
                          </Grid.Col>
                          <Grid.Col width={6}>
                            <table>
                              <tbody>
                                <tr>
                                  <td>
                                    <Switch
                                      disabled={readonly}
                                      name="settings.disable-bitmap-caching"
                                      size="small"
                                    />
                                  </td>
                                  <td>{t('Disable bitmap caching')}</td>
                                </tr>
                                <tr>
                                  <td>
                                    <Switch
                                      disabled={readonly}
                                      name="settings.disable-offscreen-caching"
                                      size="small"
                                    />
                                  </td>
                                  <td>{t('Disable off-screen caching')}</td>
                                </tr>
                                <tr>
                                  <td>
                                    <Switch
                                      disabled={readonly}
                                      name="settings.disable-glyph-caching"
                                      size="small"
                                    />
                                  </td>
                                  <td>{t('Disable glyph caching')}</td>
                                </tr>
                              </tbody>
                            </table>
                          </Grid.Col>
                        </Grid.Row>
                      </Segment>
                      <Segment heading={t('Remote App')}>
                        <Grid.Row>
                          <Grid.Col width={6}>
                            <FormGroup
                              error={
                                <ErrorMessage name="settings.remote-app-dir" />
                              }
                              label={t('Remote application directory')}
                              input={
                                <Input
                                  disabled={readonly}
                                  name="settings.remote-app-dir"
                                  value={
                                    values.settings['remote-app-dir'] || ''
                                  }
                                />
                              }
                            />
                          </Grid.Col>
                          <Grid.Col width={3}>
                            <FormGroup
                              error={
                                <ErrorMessage name="settings.remote-app" />
                              }
                              label={t('Remote application')}
                              input={
                                <Input
                                  disabled={readonly}
                                  name="settings.remote-app"
                                  value={values.settings['remote-app'] || ''}
                                />
                              }
                            />
                          </Grid.Col>
                          <Grid.Col width={3}>
                            <FormGroup
                              error={
                                <ErrorMessage name="settings.remote-app-args" />
                              }
                              label={t('Remote application arguments')}
                              input={
                                <Input
                                  disabled={readonly}
                                  name="settings.remote-app-args"
                                  value={
                                    values.settings['remote-app-args'] || ''
                                  }
                                />
                              }
                            />
                          </Grid.Col>
                        </Grid.Row>
                      </Segment>
                      <Segment heading={t('Preconnection PDU / Hyper-V')}>
                        <Grid.Row>
                          <Grid.Col width={4}>
                            <FormGroup
                              error={
                                <ErrorMessage name="settings.preconnection-id" />
                              }
                              label={t('RDP source ID')}
                              input={
                                <Input
                                  disabled={readonly}
                                  name="settings.preconnection-id"
                                  type="number"
                                  value={
                                    values.settings['preconnection-id'] || ''
                                  }
                                />
                              }
                            />
                          </Grid.Col>
                          <Grid.Col width={4}>
                            <FormGroup
                              error={
                                <ErrorMessage name="settings.preconnection-blob" />
                              }
                              label={t('Preconnection BLOB (VM ID)')}
                              input={
                                <Input
                                  disabled={readonly}
                                  name="settings.preconnection-blob"
                                  value={
                                    values.settings['preconnection-blob'] || ''
                                  }
                                />
                              }
                            />
                          </Grid.Col>
                        </Grid.Row>
                      </Segment>
                    </ProfileCollapse.Panel>
                  </ProfileCollapse>
                  <Grid.Row>
                    <Grid.Col width={12} className="mt-4">
                      <Flex h="end" v="center">
                        <SubmitButton
                          loading={isSubmitting}
                          disabled={
                            isSubmitting ||
                            hasErrors ||
                            !values.name ||
                            readonly
                          }
                        >
                          {t('Save profile')}
                        </SubmitButton>
                      </Flex>
                    </Grid.Col>
                  </Grid.Row>
                </Grid.Container>
              </Form>
            </ProfileCollapse.Panel>
          </ProfileCollapse>
        );
      }}
    </Formik>
  );
};

export default RDPCollapse;
